import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayoutV2 from '../../layouts/MainLayoutV2';
import { useTheme } from '@mui/material/styles';
import Blocks from '../../stories/layout-components/Block';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase';
import { Box, CardMedia, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from '@mui/material';
import { AccountTreeOutlined, AutoFixHighOutlined, CallToActionOutlined, DrawOutlined, NoteAddOutlined, SyncOutlined, DeleteOutline, CopyAllOutlined } from '@mui/icons-material';
import TextField from '../../stories/general-components/TextField';
import { fetchBusinessData } from '../../redux/actions-v2/coreAction';
import moment from 'moment';
import Checkbox from '../../stories/general-components/Checkbox';
import { toast } from 'react-toastify';



const ExtensionsParams = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { extensionId } = useParams();
  const [selectedModule, setSelectedModule] = useState({});
  const [installations, setInstallations] = useState([]);
  const businessPreference = useSelector((state) => state.core.businessData);
  const extension = businessPreference?.menu?.find((item) => item?.id === extensionId);
  const currentPlatform = useSelector(state => state.core.currentPlatform);

  // State for Add Installation Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newBusinessId, setNewBusinessId] = useState('');



  const handleGetData = async () => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/extensions`,
        body: {
          operation: 'get',
          extensionId: extensionId,
          extensionDetails: {
            ownerId: 'businessesOnNode/' + businessPreference?.id
          }
        },
      });

      setSelectedModule(response?.extensionData || {});
      setInstallations(response?.installations || []);
    } catch (error) {
      console.error(error);
    }
  }

  const updateModule = async () => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/extensions`,
        body: {
          operation: 'update',
          extensionId: extensionId,
          extensionDetails: {
            ownerId: 'businessesOnNode/' + businessPreference?.id
          },
          data: {
            title: selectedModule?.title,
            description: selectedModule?.description,
            basePrice: selectedModule?.basePrice,
            monthlyFee: selectedModule?.monthlyFee,
            perElementPrice: selectedModule?.perElementPrice,
            perElementPriceSuffix: selectedModule?.perElementPriceSuffix,
            moduleColor: selectedModule?.moduleColor,
            releaseDate: selectedModule?.releaseDate,
            isActive: selectedModule?.isActive
          }
        }
      });
      dispatch(fetchBusinessData(businessPreference?.id, t, i18n.language, null, currentPlatform))
    } catch (error) {
      console.error(error);
    }
  }

  const handleRemoveInstallation = async (installationId) => {
    try {
      await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/codeExtension`,
        body: {
          type: 'remove',
          extensionId: extensionId,
          installationId: installationId,
          extensionDetails: {
            ownerId: 'businessesOnNode/' + businessPreference?.id
          }
        },
      });
      handleGetData();
    } catch (error) {
      console.error('Error removing installation:', error);
    }
  };

  // --- Add Installation Modal Logic ---
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewBusinessId(''); // Reset input on close
  };

  const handleAddInstallationConfirm = async () => {
    if (!newBusinessId.trim()) {
      // Basic validation - can be enhanced
      alert(t('pleaseEnterBusinessId')); // Add translation key
      return;
    }
    try {
      await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/codeExtension`,
        body: {
          type: 'allow',
          extensionId: extensionId,
          targetId: newBusinessId.trim(),
          extensionDetails: {
            ownerId: 'businessesOnNode/' + businessPreference?.id
          }
        },
      });
      handleGetData(); // Refresh the list
      handleCloseAddModal(); // Close modal on success
      // Optional: Show success message
    } catch (error) {
      console.error('Error adding installation:', error);
      // Optional: Show error message to user
      alert(t('errorAddingInstallation')); // Add translation key
    }
  };

  const handleCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(t('copied'));
  }

  useEffect(() => {
    if (businessPreference?.id && extensionId) {
      handleGetData();
    }
  }, [businessPreference?.id, extensionId]);




  return (
    <MainLayoutV2 actions={{ save: () => updateModule() }} icon='ExtensionOutlined' pageTitle={extension?.name || ''} subTitle={t('extensionParams')}>
      <Blocks height={1} heightPercentage={95}>
        <div className='row p-3'>
          <div className='col-8'>
            <div className='row'>
              <TextField
                label={t('name')}
                value={selectedModule?.title || ''}
                onChange={(e) => setSelectedModule({ ...selectedModule, title: e.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                label={t('description')}
                value={selectedModule?.description || ''}
                onChange={(e) => setSelectedModule({ ...selectedModule, description: e.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                label={t('basePrice')}
                type='number'
                value={selectedModule?.basePrice || ''}
                onChange={(e) => setSelectedModule({ ...selectedModule, basePrice: e.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                label={t('monthlyFee')}
                type='number'
                value={selectedModule?.monthlyFee || ''}
                onChange={(e) => setSelectedModule({ ...selectedModule, monthlyFee: e.target.value })}
              />
            </div>
            {selectedModule?.feePerElement && (
              <div className='row'>
                <TextField
                  label={t('perElementPrice')}
                  type='number'
                  value={selectedModule?.perElementPrice || ''}
                  onChange={(e) => setSelectedModule({ ...selectedModule, perElementPrice: e.target.value })}
                />
                <TextField
                  label={t('perElementPriceSuffix')}
                  value={selectedModule?.perElementPriceSuffix || ''}
                  onChange={(e) => setSelectedModule({ ...selectedModule, perElementPriceSuffix: e.target.value })}
                />
              </div>
            )}
            <div className='row'>
              <TextField
                label={t('moduleColor')}
                type='color'
                value={selectedModule?.moduleColor || '#ffffff'}
                onChange={(e) => setSelectedModule({ ...selectedModule, moduleColor: e.target.value })}
              />
            </div>
            <div className='row'>
              <TextField
                label={t('releaseDate')}
                type='date'
                value={selectedModule?.releaseDate?._seconds ? moment.unix(selectedModule.releaseDate._seconds).format('YYYY-MM-DD') : ''}
                onChange={(e) => setSelectedModule({ ...selectedModule, releaseDate: { _seconds: moment(e.target.value).unix(), _nanoseconds: 0 } })}
              />
            </div>
            <div className='row'>
              <Checkbox
                label={t('isActive')}
                value={selectedModule?.isActive}
                onChange={(e) => setSelectedModule({ ...selectedModule, isActive: e.target.checked })}
              />
            </div>
            <div className='row mt-4'>
              <div className='mb-4'>
                <Typography variant="h6" sx={{ mb: 1 }}>{t('installations')}</Typography>
                <TableContainer elevation={0} component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="installations table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('id')}</TableCell>
                        <TableCell>{t('businessName')}</TableCell>
                        <TableCell>{t('installedStatus')}</TableCell>
                        <TableCell>{t('date')}</TableCell>
                        <TableCell align="right">{t('actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {installations.map((installation) => (
                        <TableRow
                          key={installation.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            {installation?.id?.slice(0, 10)}
                          </TableCell>
                          <TableCell>{installation?.businessName || 'N/A'}</TableCell>
                          <TableCell>{installation?.isInstalled ? t('active') : t('pending')}</TableCell>
                          <TableCell>
                            {installation?.timeStamp?._seconds ? moment.unix(installation?.timeStamp?._seconds).format('YYYY-MM-DD HH:mm') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {!installation.isInstalled && (
                              <Tooltip title={t('copyCode')}>
                                <span>
                                  <IconButton
                                    aria-label="copyCode"
                                    size="small"
                                    onClick={() => handleCode(installation.code)}
                                    title={t('copyCode')}
                                  >
                                    <CopyAllOutlined fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {!installation.isInstalled && (
                              <Tooltip title={t('deleteInstallation')}>
                                <span>
                                  <IconButton
                                    aria-label="delete"
                                    size="small"
                                    onClick={() => handleRemoveInstallation(installation.installationId)}
                                    title={t('removeInstallation')}
                                  >
                                    <DeleteOutline fontSize="inherit" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {installations.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">{t('noInstallationsFound')}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
              {selectedModule?.passwordRequired && (
                <div className='mb-3 mt-3'>
                  <Button disableElevation variant="contained" onClick={handleOpenAddModal} sx={{ mb: 2 }}>
                    {t('addInstallation')}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className='col-4' style={{ backgroundColor: '#F9F9F9', height: '65vh', borderRadius: '8px', padding: '15px' }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: selectedModule?.moduleColor || theme.palette.grey[300],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}>
              <CardMedia
                component="img"
                alt={selectedModule?.title || 'Extension Logo'}
                image={selectedModule?.logoUrl || 'default_image.jpg'}
                sx={{
                  width: 30,
                  height: 30,
                  objectFit: 'contain',
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Box>
            <Typography variant="h6" fontSize={'16px'} fontWeight={600} gutterBottom>{selectedModule?.title}</Typography>
            <Typography variant="body1" fontSize={'12px'} fontWeight={400} color='#69696999' lineHeight={1.1} gutterBottom>{selectedModule?.description}</Typography>
            <div className='mt-5'>
              <Typography variant="h6" fontSize={'12px'} fontWeight={600} gutterBottom>{t('pricing')}</Typography>
              {selectedModule?.basePrice ? (
                <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                  {t('basePriceShort')}: {(selectedModule?.basePrice || 0).toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                </Typography>
              ) : ('')}
              {selectedModule?.monthlyFee ? (
                <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                  {t('monthlyFeeShort')}: {(selectedModule?.monthlyFee || 0)?.toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                </Typography>
              ) : ('')}
              {selectedModule?.feePerElement ? (
                <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                  {t('perElementShort')}: {(selectedModule?.perElementPrice || 0)?.toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })} / {selectedModule?.perElementPriceSuffix || t('element')}
                </Typography>
              ) : ('')}
            </div>
            <div className='mt-4'>
              <Typography variant="h6" fontSize={'12px'} fontWeight={600} gutterBottom>{t('withThatExt')}</Typography>
              <div className='row'>
                {selectedModule?.type?.feature && (
                  <div className='d-flex middle-content'>
                    <div><NoteAddOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                    <div>
                      <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.feature + ' ' + t('module') + '(s)'}</Typography>
                    </div>
                  </div>
                )}
                {selectedModule?.type?.component && (
                  <div className='d-flex middle-content'>
                    <div><CallToActionOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                    <div>
                      <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.component + ' ' + t('component') + '(s)'}</Typography>
                    </div>
                  </div>
                )}
                {selectedModule?.type?.integration && (
                  <div className='d-flex middle-content'>
                    <div><SyncOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                    <div>
                      <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.integration + ' ' + t('integrationExt') + '(s)'}</Typography>
                    </div>
                  </div>)}
                {selectedModule?.type?.template && (
                  <div className='d-flex middle-content'>
                    <div><DrawOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                    <div>
                      <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.template + ' ' + t('template') + '(s)'}</Typography>
                    </div>
                  </div>)}
                {selectedModule?.type?.plugin && (
                  <div className='d-flex middle-content'>
                    <div><AutoFixHighOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                    <div>
                      <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.plugin + ' ' + t('automation') + '(s)'}</Typography>
                    </div>
                  </div>)}
                {selectedModule?.type?.account && (
                  <div className='d-flex middle-content'>
                    <div><AccountTreeOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                    <div>
                      <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.account + ' ' + t('account') + '(s)'}</Typography>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* --- Monthly Revenue Forecast --- */}
            <div className='mt-4'>
              <Typography variant="h6" fontSize={'12px'} fontWeight={600} gutterBottom>{t('monthlyRevenueForecast')}</Typography>
              {(() => {
                const activeInstallations = installations.filter(inst => inst.isInstalled);
                const monthlyFee = Number(selectedModule?.monthlyFee || 0);
                const mrr = activeInstallations.length * monthlyFee;

                return (
                  <>
                    <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                      {t('activeInstallationsCount')}: {activeInstallations.length}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                      {t('estimatedMrr')}: {mrr.toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                    </Typography>
                    {selectedModule?.feePerElement && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '10px' }}>
                        ({t('forecastExcludesPerElement')})
                      </Typography>
                    )}
                  </>
                );
              })()}
            </div>
            {/* --- End Monthly Revenue Forecast --- */}

          </div>
        </div>
      </Blocks>

      {/* Add Installation Modal */}
      <Dialog open={isAddModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>{t('addNewInstallation')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('enterTargetBusinessId')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="businessId"
            label={t('businessId')}
            type="text"
            fullWidth
            variant="standard"
            value={newBusinessId}
            onChange={(e) => setNewBusinessId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disableElevation onClick={handleCloseAddModal}>{t('cancel')}</Button>
          <Button disableElevation onClick={handleAddInstallationConfirm} variant="contained">{t('add')}</Button>
        </DialogActions>
      </Dialog>

    </MainLayoutV2 >
  );
};

export default ExtensionsParams;